import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getStaticQuestions, PaginatedStaticQuestionResponse } from '../../Game/api/gameApi';
import Pagination from './Pagination';

const PAGE_SIZE = 20;

interface QuestionPickerProps {
  selectedIds: Set<number>;
  onChange: (next: Set<number>) => void;
}

const QuestionPicker: React.FC<QuestionPickerProps> = ({ selectedIds, onChange }) => {
  const [data, setData] = useState<PaginatedStaticQuestionResponse | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const requestIdRef = useRef(0);

  const loadData = useCallback(async (targetPage: number, targetSearch: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await getStaticQuestions({ page: targetPage, pageSize: PAGE_SIZE, search: targetSearch || undefined });
      if (requestId !== requestIdRef.current) return;
      setData(res);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setData(null);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  useEffect(() => {
    loadData(page, search);
  }, [page, search, loadData]);

  const toggle = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const currentPageIds = data?.results.map((q) => q.id) ?? [];
  const allOnPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.has(id));

  const toggleSelectAllOnPage = () => {
    const next = new Set(selectedIds);
    if (allOnPageSelected) {
      currentPageIds.forEach((id) => next.delete(id));
    } else {
      currentPageIds.forEach((id) => next.add(id));
    }
    onChange(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search question text..."
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-500">{selectedIds.size} question(s) selected</span>
      </div>

      <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left w-8">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleSelectAllOnPage}
                  className="h-4 w-4 text-blue-600 rounded"
                  title="Select all on this page"
                />
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Guardian</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Question</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SDGs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">Loading...</td></tr>
            ) : !data || data.results.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No questions found</td></tr>
            ) : (
              data.results.map((q) => (
                <tr
                  key={q.id}
                  onClick={() => toggle(q.id)}
                  className={`cursor-pointer hover:bg-blue-50 ${selectedIds.has(q.id) ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(q.id)}
                      onChange={() => toggle(q.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                  </td>
                  <td className="px-3 py-2 text-gray-900">{q.id}</td>
                  <td className="px-3 py-2 text-gray-600">{q.guardian || 'N/A'}</td>
                  <td className="px-3 py-2 text-gray-900 max-w-xs truncate">{q.question_text}</td>
                  <td className="px-3 py-2 text-gray-600">{q.sdg_numbers?.join(', ') || 'N/A'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && data.count > 0 && (
        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
          <span className="text-xs text-gray-500">
            {data.count} question(s){search ? ' matched' : ''}
          </span>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};

export default QuestionPicker;